import {
  SentenceOffset,
  SetError,
  SetIsLoading,
  WtPusherEvent,
} from '@chegg-wt/core-wa/state/types'
import { FeedbackType } from '@chegg-wt/core-wa/types'
import {
  SYNONYM_REWRITE_FETCH_RETRY_COUNT,
  SELECTION_CHECK_TIMEOUT,
} from '@chegg-wt/core-wa/constants/paraphraser'
import { oneGraph } from '@chegg-wt/core/api/oneGraph'
import { CheckTypeInput } from '@chegg-wt/core/globalTypesOneGraph'
import getFeatureEditor from '../getEditor'

interface Sentence extends SentenceOffset {
  suggestion?: string
}

class ParaphraseSelectionDataHandler {
  private selectedSentences: Sentence[]

  private expectedSentencesCount: number = 0

  private timeout: ReturnType<typeof setTimeout>

  private retriesLeft = {}

  private traceId: string

  private paperId: string

  private initiatedAt: string

  private checkCompletion = (
    handleParaphraseCompletion: (originalText: string, suggestedText: string) => void
  ) => {
    if (!Number.isNaN(this.expectedSentencesCount) && this.expectedSentencesCount === 0) {
      // Completed, send result
      const editor = getFeatureEditor()
      const start = this.selectedSentences[0].offset
      const {
        offset: endSentenceOffset,
        length: endSentenceLength,
      } = this.selectedSentences[this.selectedSentences.length - 1]
      const end = endSentenceOffset + endSentenceLength

      // -1 at all indexes as tiptap sentences are 1 based indexed
      const originalText = editor.getText().substring(start - 1, end - 1)
      let suggestedText = ''
      let prevEnd = 0
      this.selectedSentences.forEach((sentence) => {
        suggestedText +=
          originalText.substring(prevEnd, sentence.offset - start) +
          (sentence.suggestion || '')
        prevEnd = sentence.offset - start + sentence.length
      })
      handleParaphraseCompletion(originalText, suggestedText)
      this.disableParaphraseFeedbackAcceptance()
    }
  }

  private clearExistingTimeout = () => {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  public disableParaphraseFeedbackAcceptance = () => {
    this.selectedSentences = null
    this.expectedSentencesCount = 0
    this.initiatedAt = null
    this.clearExistingTimeout()
  }

  public enableParaphraseFeedbackAcceptance = (
    selectedSentences: SentenceOffset[],
    initiatedAt: string,
    traceId: string,
    paperId: string
  ) => {
    if (!selectedSentences?.length) return
    this.expectedSentencesCount = selectedSentences.length
    this.selectedSentences = selectedSentences.map((sentence) => ({ ...sentence }))
    this.initiatedAt = initiatedAt
    this.traceId = traceId
    this.paperId = paperId
    this.selectedSentences.forEach((sentence) => {
      this.retriesLeft[sentence.id] = SYNONYM_REWRITE_FETCH_RETRY_COUNT
    })
  }

  public handleParaphraseCall = (
    traceId: string,
    paperId: string,
    selectedSentences: Sentence[],
    initiatedAt: string,
    setError: SetError,
    setIsLoading: SetIsLoading,
    checkType = CheckTypeInput.PARAPHRASE_SELECTION
  ) => {
    // Clear old timeout for this rewrite
    this.clearExistingTimeout()

    // Add a new timeout
    this.timeout = setTimeout(() => {
      setError({ code: 'TIMEOUT', message: 'Timeout in fetching paraphrased sentences' })
      setIsLoading(false)
      this.disableParaphraseFeedbackAcceptance()
    }, SELECTION_CHECK_TIMEOUT)

    oneGraph
      .sentenceCheck({
        request: {
          checkType,
          traceId,
          paperId,
          sentences: selectedSentences.map((sentence) => ({
            id: sentence.id,
            originalText: sentence.originalText,
          })),
          initiatedAt,
        },
      })
      .catch((error: Error) => {
        setError({ code: 'SERVER_ERROR', message: error.message })
        setIsLoading(false)
        this.disableParaphraseFeedbackAcceptance()
      })
  }

  private handleRetry(
    sentence: Sentence[],
    setError: SetError,
    setIsLoading: SetIsLoading
  ) {
    this.handleParaphraseCall(
      this.traceId,
      this.paperId,
      sentence,
      this.initiatedAt,
      setError,
      setIsLoading
    )

    this.retriesLeft[sentence[0].id] -= 1
  }

  public handleParaphraseFeedbackEvent = (
    eventData: WtPusherEvent,
    handleParaphraseCompletion: (originalText: string, SuggestedText: string) => void,
    setError: SetError,
    setIsLoading: SetIsLoading
  ): void => {
    const { feedback, initiatedAt } = eventData
    // we will only consider paraphrase events when results are expected and with same initiated time
    if (initiatedAt === this.initiatedAt && this.expectedSentencesCount > 0) {
      const sentence = this.selectedSentences.find(
        (sentence) => sentence.id === eventData.sentence.id
      )

      if (!sentence) return

      if (eventData.error) {
        if (this.retriesLeft[sentence.id] > 0) {
          this.handleRetry([sentence], setError, setIsLoading)
          return
        }
        setError({ code: 'API_ERROR', message: eventData.error.message })
        setIsLoading(false)
        this.disableParaphraseFeedbackAcceptance()
        return
      }

      if (feedback) {
        // We will only consider events for required sentences
        if (!sentence.suggestion) this.expectedSentencesCount -= 1
        if (feedback.feedbackType === FeedbackType.PARAPHRASE)
          sentence.suggestion = feedback.suggestedText
        this.checkCompletion(handleParaphraseCompletion)
      }
    }
  }
}

const paraphraseSelectionDataHandler = new ParaphraseSelectionDataHandler()
export default paraphraseSelectionDataHandler
